import {
    GridPreferencePanelsValue,
    GridRowCount,
    GridRowModes,
    GridSelectedRowCount,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
    gridPreferencePanelStateSelector,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { ITableState } from "../EntityTable";
import "../EntityTable.css";
import {
    alpha,
    Autocomplete,
    autocompleteClasses,
    AutocompleteRenderInputParams,
    Box,
    Button,
    Divider,
    FilterOptionsState,
    IconButton,
    InputBase,
    Menu,
    styled,
    TablePagination,
    TablePaginationProps,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavigateOptions, To } from "react-router-dom";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import { useEntityTable } from "@mirage/store/entityTableStore";
import { PredefinedEntityTableView } from "@shared/mirage/services/IUserPreferencesService";
import { EntityHelper } from "../EntityTableHelper";
import { UserDefinedButton } from "./UserDefinedButton";

export interface CustomGridToolbarProps {
    rowMode: "view" | GridRowModes;
    totalRecords: number;
    setTableState: React.Dispatch<React.SetStateAction<ITableState>>;
    anchorEl: HTMLElement | null;
    setAnchorEl: (el: HTMLElement | null) => void;
    getOptionButtonItems?: () => JSX.Element[];
    tableTitle?: string;
    /**
     * @default true
     */
    inlineTitle?: boolean;
    options?: CustomGridToolbarOptions;
    paginationProps: TablePaginationProps;
    showAll?: boolean;
    showPagination?: boolean;
    quickSearch?: boolean;
    quickSearchAutofocus?: boolean;
}

const CustomGridToolbar: React.FC<CustomGridToolbarProps> = memo(
    ({
        options,
        paginationProps,
        setTableState,
        totalRecords,
        tableTitle,
        showAll = false,
        quickSearch = false,
        showPagination = true,
        inlineTitle = true,
        anchorEl,
        setAnchorEl,
        getOptionButtonItems,
        quickSearchAutofocus = true,
    }) => {
        const { experimentalQueryBuilder } = useEntityTable();
        const paginationWidth = experimentalQueryBuilder ? "fit-content" : undefined;
        const paginationFlex = experimentalQueryBuilder ? undefined : "1";
        const showOptionsButton = !options?.disableOptions;
        return (
            <GridToolbarContainer className="entityTable__toolbar" style={{ display: "flex", alignItems: "center" }}>
                <div style={{ flex: "1", display: "inline-flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                    <Actions setTableState={setTableState} options={options} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
                    <PredefinedEntityTableViews />
                    {quickSearch ? <GridToolbarQuickFilter autoFocus={quickSearchAutofocus} debounceMs={500} /> : null}
                </div>
                {!experimentalQueryBuilder ? (
                    <div style={{ flex: "0 1 auto", textAlign: "center" }}>
                        <EntityTableTitle tableTitle={tableTitle} inlineTitle={inlineTitle} />
                    </div>
                ) : null}
                <div style={{ flex: paginationFlex, display: "flex", justifyContent: "flex-end", alignItems: "center", width: paginationWidth }}>
                    <Pagination paginationProps={paginationProps} totalRecords={totalRecords} showAll={showAll} showPagination={showPagination} />
                    {showOptionsButton ? <OptionsButton getOptionButtonItems={getOptionButtonItems} /> : null}
                </div>
            </GridToolbarContainer>
        );
    }
);

const Pagination = memo(
    ({ totalRecords, paginationProps, showAll, showPagination }: Pick<CustomGridToolbarProps, "totalRecords" | "paginationProps" | "showAll" | "showPagination">) => {
        const { handleOnPageChange } = useEntityTable();
        const clickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
        const { current } = useGridApiContext();
        const onPageChange = useCallback(
            (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
                if (clickTimeout.current) {
                    event?.preventDefault();
                    return;
                }
                clickTimeout.current = setTimeout(() => {
                    handleOnPageChange(event, page);
                    clickTimeout.current = null;
                }, 250);
            },
            [handleOnPageChange]
        );
        const onPreviousDoubleClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
            if (clickTimeout.current) {
                clearTimeout(clickTimeout.current);
                clickTimeout.current = null;
                handleOnPageChange(event, 0);
            }
        };
        if (!totalRecords || !showPagination) {
            return <div className="entityTable__toolbar__pagination-empty"></div>;
        }
        if (showAll) {
            const rowSelectionModelCount = current.getSelectedRows();
            return (
                <Box flex={1} display={"flex"} justifyContent={"flex-end"}>
                    {rowSelectionModelCount.size ? <GridSelectedRowCount selectedRowCount={rowSelectionModelCount.size} /> : null}
                    <GridRowCount rowCount={totalRecords} visibleRowCount={totalRecords} />
                </Box>
            );
        }

        return (
            <TablePagination
                key="grid-pagination-toolbar"
                {...paginationProps}
                className="entityTable__toolbar__pagination"
                showFirstButton={false}
                showLastButton={false}
                rowsPerPageOptions={[]}
                onPageChange={onPageChange}
                backIconButtonProps={{ onDoubleClick: onPreviousDoubleClick }}
            />
        );
    }
);

const EntityTableTitle = memo(({ tableTitle, inlineTitle }: Pick<CustomGridToolbarProps, "tableTitle" | "inlineTitle">) => {
    if (!tableTitle) {
        return null;
    }
    return (
        <Typography variant="h6" className="entityTable__toolbar__title" flexBasis={!inlineTitle ? "100% !important" : undefined}>
            {tableTitle}
        </Typography>
    );
});

const Actions = memo((props: Pick<CustomGridToolbarProps, "options" | "setTableState" | "anchorEl" | "setAnchorEl">) => {
    const { isMobile } = useDeviceDetect();
    const { options, setTableState, anchorEl, setAnchorEl } = props;
    const currentTheme = useTheme();
    const isPublicOnlyMode = !!currentTheme.entityTable?.publicOnly;
    const showColumnsButton = !options?.disableColumns && !isPublicOnlyMode;
    const showFilterButton = !options?.disableFilter && !isPublicOnlyMode;
    const showOptionsButton = !options?.disableOptions;
    const showNewRecordButton = !!options?.newRecord;
    const showCustomToolbarActionButtons = !!options?.customButtons;
    const placeholder = <div className="entityTable__toolbar__placeholder"></div>;
    const apiRef = useGridApiContext();
    const handleColumnsButtonOnClick = useCallback(() => {
        const columnHeaderEl = apiRef.current?.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders") as HTMLElement;
        setAnchorEl(columnHeaderEl as HTMLElement);
    }, [apiRef, setAnchorEl]);
    const handleFilterButtonOnClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const preferencePanelState = gridPreferencePanelStateSelector(apiRef.current.state);
            if (anchorEl?.id === event.currentTarget.id && preferencePanelState.openedPanelValue === GridPreferencePanelsValue.filters) {
                apiRef.current.hideFilterPanel();
                return;
            }
            const columnHeaderEl = apiRef.current.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders") as HTMLElement;
            columnHeaderEl.id = "gridFilterButton";
            setAnchorEl(columnHeaderEl);
            apiRef.current.showFilterPanel();
        },
        [anchorEl, apiRef, setAnchorEl]
    );
    const noActions = !showColumnsButton && !showFilterButton && !showNewRecordButton && !showOptionsButton && !showCustomToolbarActionButtons;

    if (noActions || isMobile) {
        return null;
    }

    return (
        <div className="entityTable__toolbar__actions">
            {showNewRecordButton ? <NewRecordButton key="grid-new-record-button-toolbar" setTableState={setTableState} {...options?.newRecord} /> : placeholder}
            {showCustomToolbarActionButtons ? <CustomToolbarActionButtons customButtons={options?.customButtons} /> : placeholder}
        </div>
    );
});

const CustomToolbarActionButtons: React.FC<{ customButtons?: JSX.Element[] | undefined }> = memo(({ customButtons }) => {
    return (
        <div style={{ display: "flex" }}>
            {customButtons?.map((btn, idx) => (
                <div key={idx}>{btn}</div>
            ))}
        </div>
    );
});

const FilterButton = memo(({ disableFilter, handleClick }: { disableFilter?: boolean; handleClick: (event: React.MouseEvent<HTMLElement>) => void }) => {
    if (disableFilter) {
        return null;
    }
    return (
        <GridToolbarFilterButton
            key="grid-toolbar-filter-button"
            componentsProps={{
                button: {
                    onClickCapture: handleClick,
                    id: "gridFilterButton",
                },
            }}
        />
    );
});

const NewRecordButton: React.FC<NewRecordButtonProps> = ({ setTableState, inline, panelType, navigate, onClick, disabled, caption, isLoading = false, icon }) => {
    const buttonIcon = icon ? icon : MirageTheme.icons.plusIcon;
    const navigateTo = useStableNavigate();
    if (inline || panelType || !!navigate || onClick) {
        return (
            <Button color="primary" disabled={disabled} startIcon={<FontAwesomeIcon icon={buttonIcon} />} onClick={onButtonClicked} loading={isLoading}>
                {caption || "Add record"}
            </Button>
        );
    }
    return null;

    function onButtonClicked() {
        if (panelType) {
            renderFormPanel();
            return;
        }
        if (inline) {
            setTableState((prevState) => {
                const minId = Math.min(...prevState.rows.map((row) => row.id));
                const id = minId > 0 ? -1 : minId - 1;
                return {
                    ...prevState,
                    rows: [...prevState.rows, { id, isNew: true, ...inline.columnsWithInitialValues }],
                    total: (prevState.total += 1),
                    rowModesModel: { ...prevState.rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: inline.fieldToFocus } },
                };
            });
        } else if (navigate?.to) {
            navigateTo(navigate.to, navigate.options);
        } else if (onClick) {
            onClick();
        }
    }

    function renderFormPanel() {
        if (panelType && !panelType?.togglePanel) {
            throw Error("Panel type adding record require togglePanel to be defined");
        }
        panelType?.togglePanel((prev) => !prev);
    }
};

const OptionsButton = memo(({ getOptionButtonItems }: Pick<CustomGridToolbarProps, "getOptionButtonItems">) => {
    const currentTheme = useTheme();
    const isPublicOnlyMode = !!currentTheme.entityTable?.publicOnly;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOnClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), []);
    const handleCloseMenu = useCallback(() => setAnchorEl(null), []);

    if (isPublicOnlyMode || !getOptionButtonItems) {
        return null;
    }
    return (
        <>
            <IconButton key="grid-toolbar-options-button" onClick={handleOnClick}>
                <FontAwesomeIcon icon={MirageTheme.icons.gearIcon} color={MirageTheme.palette.primary.main} style={MirageTheme.icons.sizeM} />
            </IconButton>
            <Menu id="gridOptionsMenu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                {getOptionButtonItems()}
            </Menu>
        </>
    );
});

const PredefinedEntityTableViews = memo(() => {
    const { experimentalQueryBuilder, selectPredefinedEntityTable, tableState, orderedPredefinedTableViews } = useEntityTable();
    const { predefinedEntityTableViews, selectedPredefinedEntityTableViewId } = tableState;
    const predefinedEntityTableViewsExists = Array.isArray(predefinedEntityTableViews) && predefinedEntityTableViews.length > 0;
    const theme = useTheme();
    const [visibleButtonsCount, setVisibleButtonsCount] = useState(0);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const moreButtonRef = useRef<HTMLButtonElement | null>(null);
    const hiddenViewsCount = predefinedEntityTableViews.slice(visibleButtonsCount).length;
    const moreButtonLabel = `${hiddenViewsCount} More`;
    const selectedView = predefinedEntityTableViews.find((view) => view.id === selectedPredefinedEntityTableViewId);
    const isSelectedViewVisible = predefinedEntityTableViews.some((view, idx) => view.id === selectedPredefinedEntityTableViewId && idx < visibleButtonsCount);
    const visibleViewsList: PredefinedEntityTableView[] = useMemo(() => {
        if (!isSelectedViewVisible && selectedView && visibleButtonsCount > 0) {
            const listWithoutLastElement = orderedPredefinedTableViews.slice(0, visibleButtonsCount - 1);
            listWithoutLastElement.push(selectedView);
            return listWithoutLastElement;
        }
        return orderedPredefinedTableViews.slice(0, visibleButtonsCount);
    }, [isSelectedViewVisible, orderedPredefinedTableViews, selectedView, visibleButtonsCount]);
    const getTextWidth = useCallback((text: string, font?: string) => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) {
            console.error("Could not get 2d context from canvas");
            return 0;
        }
        context.font = font || getComputedStyle(document.body).font;
        canvas.remove();
        return context.measureText(text).width;
    }, []);
    const handleResize = useCallback(() => {
        const containerWidth = containerRef.current?.offsetWidth || 0;
        const moreButtonWidth = 32 + getTextWidth(moreButtonLabel);
        let accumulatedWidth = moreButtonWidth;
        let visibleCount = 0;

        predefinedEntityTableViews.forEach((view) => {
            const buttonWidthPixels = 32 + getTextWidth(view.name);
            const sum = accumulatedWidth + buttonWidthPixels;
            if (sum <= containerWidth) {
                visibleCount++;
            }
            accumulatedWidth += buttonWidthPixels;
        });
        setVisibleButtonsCount(visibleCount);
    }, [getTextWidth, moreButtonLabel, predefinedEntityTableViews]);
    const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => setMenuAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setMenuAnchorEl(null);
    const showMoreButton = visibleButtonsCount < predefinedEntityTableViews.length;
    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => <StyledInput ref={params.InputProps.ref} inputProps={params.inputProps} autoFocus placeholder="Filter views" />,
        []
    );
    const renderOption = useCallback(
        (_props: React.HTMLAttributes<HTMLLIElement>, option: PredefinedEntityTableView) => {
            return (
                <Tooltip title={option.name} key={option.id}>
                    <Box
                        sx={{
                            padding: "6px",
                            paddingLeft: "15px",
                            ":hover": {
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                cursor: "pointer",
                            },
                        }}
                        onClick={() => {
                            selectPredefinedEntityTable(option.id);
                            handleCloseMenu();
                        }}
                    >
                        {EntityHelper.formatViewNameToMaxLength(option.name, "list")}
                    </Box>
                </Tooltip>
            );
        },
        [selectPredefinedEntityTable, theme.palette.action.hover, theme.palette.text.primary]
    );
    const getOptionLabel = useCallback((option: PredefinedEntityTableView) => option.name, []);
    const filterOptions = useCallback((options: PredefinedEntityTableView[], state: FilterOptionsState<PredefinedEntityTableView>) => {
        return options.filter((option) => {
            if (!option.name && !option.id) {
                console.error("option is missing name and id", option);
                return false;
            }
            if (state.inputValue === "") {
                return true;
            }
            const captionMatch = option.name?.toLowerCase().includes(state.inputValue.toLowerCase());
            return captionMatch;
        });
    }, []);
    const isOptionEqualToValue = useCallback((o: PredefinedEntityTableView, v: PredefinedEntityTableView) => o.name === v.name, []);
    const getBtnVariantStyles = (variant: "outlined" | "contained", backgroundColor?: string, colorChanged?: boolean) => {
        const btnBackgroundColor = backgroundColor ? backgroundColor : MirageTheme.palette.primary.main;
        switch (variant) {
            case "outlined":
                return {
                    borderTop: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderRight: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderBottom: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderLeft: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    backgroundColor: colorChanged ? btnBackgroundColor : "transparent",
                    color: colorChanged ? theme.palette.getContrastText(btnBackgroundColor) : "black",
                };
            case "contained":
            default:
                return {
                    color: "black",
                    backgroundColor: "transparent",
                    borderRight: `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderLeft: `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderBottom: undefined,
                    borderTop: `4px solid ${btnBackgroundColor}`,
                };
        }
    };

    useEffect(() => {
        const observer = new ResizeObserver(handleResize);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, [handleResize]);

    if (!experimentalQueryBuilder) {
        return null;
    }

    return (
        <div className="entityTable__toolbar__predefinedTables" style={{ display: "flex", flexWrap: "nowrap", overflow: "hidden", whiteSpace: "nowrap" }} ref={containerRef}>
            <div
                style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxHeight: "36.5px",
                    display: "inline-flex",
                    alignItems: "center",
                }}
            >
                {predefinedEntityTableViewsExists
                    ? visibleViewsList.map((tableView, idx) => {
                          const btnVariant = selectedPredefinedEntityTableViewId === tableView.id ? "contained" : "outlined";
                          const btnBorderBottom = idx === visibleViewsList.length - 1 ? undefined : `1px solid ${alpha(theme.palette.primary.main, 0.5)}`;
                          return (
                              <React.Fragment key={tableView.id}>
                                  <Tooltip title={tableView.description}>
                                      <UserDefinedButton
                                          style={{
                                              maxHeight: "36.5px",
                                              flexShrink: 0,
                                              borderBottomLeftRadius: "0",
                                              borderBottomRightRadius: "0",
                                              ...getBtnVariantStyles(btnVariant, tableView.color, tableView.colorChanged),
                                          }}
                                          onClick={() => selectPredefinedEntityTable(tableView.id)}
                                      >
                                          {EntityHelper.formatViewNameToMaxLength(tableView.name, "toolbar")}
                                      </UserDefinedButton>
                                  </Tooltip>
                                  <div style={{ width: "5px", borderBottom: btnBorderBottom, height: "100%" }}></div>
                              </React.Fragment>
                          );
                      })
                    : null}
                <Button variant="outlined" onClick={handleMoreClick} ref={moreButtonRef} style={{ display: showMoreButton ? "initial" : "none" }}>
                    {moreButtonLabel}
                </Button>
                <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
                    <Autocomplete
                        open
                        filterOptions={filterOptions}
                        isOptionEqualToValue={isOptionEqualToValue}
                        PopperComponent={PopperComponent}
                        renderOption={renderOption}
                        options={predefinedEntityTableViews}
                        renderInput={renderInput}
                        getOptionLabel={getOptionLabel}
                    />
                    <Divider />
                </Menu>
            </div>
        </div>
    );
});

const StyledInput = styled(InputBase)(({ theme }) => ({
    padding: 10,
    width: "100%",
    borderBottom: "1px solid",
    borderBottomColor: "#30363d",
    ...theme.applyStyles("light", {
        borderBottomColor: "#eaecef",
    }),
    "& input": {
        borderRadius: 4,
        backgroundColor: "#0d1117",
        padding: 8,
        transition: theme.transitions.create(["border-color", "box-shadow"]),
        border: "1px solid",
        borderColor: "#30363d",
        fontSize: 14,
        ...theme.applyStyles("light", {
            backgroundColor: "#fff",
            borderColor: "#eaecef",
        }),
        "&:focus": {
            boxShadow: "0px 0px 0px 3px rgb(12, 45, 107)",
            borderColor: "#388bfd",
            ...theme.applyStyles("light", {
                boxShadow: "0px 0px 0px 3px rgba(3, 102, 214, 0.3)",
                borderColor: "#0366d6",
            }),
        },
    },
}));
const PopperComponent = (props: PopperComponentProps) => {
    const { disablePortal, anchorEl, open, ...other } = props;
    return <StyledAutocompletePopper {...other} />;
};

const StyledAutocompletePopper = styled("div")(({ theme }) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: "none",
        margin: 0,
        color: "inherit",
        fontSize: 13,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: "#1c2128",
        padding: 0,
        ...theme.applyStyles("light", {
            backgroundColor: "#fff",
        }),
        [`& .${autocompleteClasses.option}`]: {
            minHeight: "auto",
            alignItems: "flex-start",
            padding: 8,
            borderBottom: "1px solid",
            borderBottomColor: "#30363d",
            ...theme.applyStyles("light", {
                borderBottomColor: "#eaecef",
            }),
            "&[aria-selected='true']": {
                backgroundColor: "transparent",
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
                backgroundColor: theme.palette.action.hover,
            },
        },
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: "relative",
    },
}));

interface PopperComponentProps {
    anchorEl?: any;
    disablePortal?: boolean;
    open: boolean;
}

interface NewRecordButtonProps extends Partial<IEntityTableAddRecord> {
    setTableState: React.Dispatch<React.SetStateAction<ITableState>>;
}

export type IEntityTableAddRecord = {
    inline?: IEntityTableAddInlineRecord;
    navigate?: { to: To; options?: NavigateOptions };
    panelType?: NewRecordPanel;
    onClick?: () => void;
    disabled?: boolean;
    caption?: string;
    isLoading?: boolean;
    icon?: IconDefinition;
};

type IEntityTableAddInlineRecord = {
    fieldToFocus?: string;
    columnsWithInitialValues?: {
        [column: string]: string;
    };
};

export type NewRecordPanel = {
    togglePanel: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CustomGridToolbarOptions = {
    customButtons?: JSX.Element[];
    newRecord?: IEntityTableAddRecord;
    disableFilter?: boolean;
    disableColumns?: boolean;
    disableOptions?: boolean;
};

export default CustomGridToolbar;
